import { FC, useState, KeyboardEvent, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAtom } from "jotai";
import { copilotPrefillAtom } from "./copilotAtoms";

interface MessageInputProps {
  onSend: (message: string) => Promise<boolean>;
  disabled?: boolean;
  loading?: boolean;
  autoFocus?: boolean;
  onNewChat?: () => void;
  hasMessages?: boolean;
}

export const MessageInput: FC<MessageInputProps> = ({
  onSend,
  disabled,
  loading,
  autoFocus = false,
  onNewChat,
  hasMessages = false,
}) => {
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [prefill, setPrefill] = useAtom(copilotPrefillAtom);

  // Consume prefill message when set
  useEffect(() => {
    if (prefill) {
      setMessage(prefill);
      setPrefill(null);
      // Focus so user can edit before sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [prefill, setPrefill]);

  // Focus input when autoFocus changes to true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const handleSend = async () => {
    if (message.trim() && !disabled && !loading) {
      const success = await onSend(message.trim());
      // Only clear input if send was successful
      if (success) {
        setMessage("");
      }
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 2,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      {hasMessages && onNewChat && (
        <Tooltip title="Start new conversation">
          <IconButton
            onClick={onNewChat}
            disabled={disabled || loading}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      )}
      <TextField
        fullWidth
        multiline
        maxRows={4}
        placeholder="Ask me anything about Catalog Machine..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        disabled={disabled || loading}
        inputRef={inputRef}
      />
      <Box>
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!message.trim() || disabled || loading}
        >
          {loading ? <CircularProgress size={24} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};
